import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { apiFetch } from './api';
import { useMe } from './auth';

export type CaseProgress = {
  eventCount: number;
  slots: {
    hasAgreement: boolean;
    hasPayment: boolean;
    hasDefect: boolean;
    hasAbandonmentOrMissedDeadline: boolean;
  };
  missing: string[];
  consistency: {
    id: string;
    ok: boolean;
    explanation: string;
    event_count_at_check: number;
    model: string;
    created_at: string;
    stale: boolean;
  } | null;
};

export const progressQueryKey = (caseId: string) => ['cases', caseId, 'progress'] as const;

export function useProgress(caseId: string | undefined) {
  const me = useMe();
  return useQuery({
    queryKey: progressQueryKey(caseId ?? ''),
    queryFn: () => apiFetch<CaseProgress>(`/cases/${caseId}/progress`),
    enabled: !!caseId && !!me.data?.user,
    // Re-poll while a background consistency check is likely still running.
    // 4 seconds is fast enough that the user sees the result land without
    // them having to refresh.
    refetchInterval: (q) => {
      const data = q.state.data;
      if (!data) return false;
      // If the latest check is stale, the server likely has a check in flight;
      // poll until it lands.
      if (data.consistency?.stale) return 4_000;
      // No events yet but recent activity? Poll briefly so the first check appears.
      if (data.eventCount > 0 && !data.consistency) return 4_000;
      return false;
    },
  });
}

/** Trigger a refetch — call this after chat finishes. */
export function useInvalidateProgress() {
  const qc = useQueryClient();
  return (caseId: string) => qc.invalidateQueries({ queryKey: progressQueryKey(caseId) });
}

/** Triggers a server-side consistency rerun + refetches progress.
 *
 * Idempotent on the server (short-circuits when the latest stored check
 * already covers the current event count), so callers don't have to
 * deduplicate aggressively. Used internally by `useAutoRecheckConsistency`. */
export function useRecheckConsistency() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (caseId: string) =>
      apiFetch<{ consistency: CaseProgress['consistency'] }>(`/cases/${caseId}/progress/recheck`, {
        method: 'POST',
        body: {},
      }),
    onSuccess: (_, caseId) => qc.invalidateQueries({ queryKey: progressQueryKey(caseId) }),
  });
}

/**
 * Auto-fires a consistency recheck whenever the timeline gains/loses events
 * and no fresh check covers the current state.
 *
 * The server already fire-and-forgets a check after each chat turn, but
 * timeline edits via the timeline pane and events inserted by document
 * extraction don't go through that path — without this hook the chronology
 * verdict would silently go stale until the user clicked the now-removed
 * "Re-check chronology" button.
 *
 * Behaviour:
 *   - Debounced 1500ms so a burst of new events (e.g. a PDF extraction
 *     that drops in 6 events) coalesces into a single recheck.
 *   - Deduplicated by `eventCount`: we won't re-fire while a request is
 *     already in flight for the same count.
 *   - The dedup flag is cleared as soon as a fresh check (stale === false)
 *     lands, so the next event-count change re-arms the trigger. */
export function useAutoRecheckConsistency(
  caseId: string | undefined,
  progress: CaseProgress | undefined,
) {
  const recheck = useRecheckConsistency();
  const lastTriggeredCountRef = useRef<number | null>(null);

  // Clear the dedup flag once a fresh check covers the current count.
  // Without this we'd never re-fire after the first round-trip lands.
  // biome-ignore lint/correctness/useExhaustiveDependencies: granular id + stale deps avoid retrigger storms
  useEffect(() => {
    const c = progress?.consistency;
    if (c && !c.stale) lastTriggeredCountRef.current = null;
  }, [progress?.consistency?.id, progress?.consistency?.stale]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: granular deps; recheck.mutate is stable per react-query
  useEffect(() => {
    if (!caseId || !progress) return;
    if (progress.eventCount === 0) return;

    const c = progress.consistency;
    const stale = !c || c.stale;
    if (!stale) return;

    // Already triggered for this exact event count? Wait for the in-flight
    // check to land before considering another fire.
    if (lastTriggeredCountRef.current === progress.eventCount) return;

    const handle = setTimeout(() => {
      lastTriggeredCountRef.current = progress.eventCount;
      recheck.mutate(caseId);
    }, 1500);

    return () => clearTimeout(handle);
  }, [
    caseId,
    progress?.eventCount,
    progress?.consistency?.id,
    progress?.consistency?.stale,
    recheck,
  ]);
}
